import { RefObject } from 'react';
import Button from 'src/components/buttons/button';
import ActionsDropdown from './actionsDropdown';
import LikeButton from 'src/components/buttons/likeButton';
import FollowButton from 'src/components/buttons/followButton';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';
import ModalType from 'src/constants/modalTypes';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'src/types';
import signupContext from 'src/constants/signupContext';
import { userHasPermission, useIsCommunityMember } from 'src/utils/context';
import styled from 'styled-components';
import { textLinkPrimary } from 'styles-js/mixins/typography';
import i18n from 'src/utils/translate';

export default function PostActions({respondRef}: {respondRef: RefObject<HTMLElement>}) {
  const dispatch = useDispatch();
  const { currentCommunity, currentUser } = useSelector(({context}: State) => context);
  const { post } = useSelector(({pages}: State) => pages.singlePost);
  const isCommunityMember = useIsCommunityMember();

  const isCurrentUserPost = post.author.id === currentUser?.userId;
  const canReport = userHasPermission(currentUser, 'SendReportAbuse');
  const canFollow = currentUser &&
    post.author.id !== currentUser?.userId &&
    //private post can only be followed by group members
    (!post.isPrivate || (post.isPrivate && isCommunityMember)) &&
    !post.isLocked;

  const onReplyClick = () => {
    if (!!respondRef && !!currentUser) {
      respondRef.current?.focus();
    } else {
      const replyContext = {
        element: signupContext.element.REPLY,
        section: signupContext.section.POST,
      };
      trackFootfallEvent(EventType.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: replyContext,
      });
      dispatch(showSignupModal(replyContext));
    }
  };

  const onReportClick = () => {
    if (currentUser) {
      trackFootfallEvent(EventType.CLICKED, {
        clickType: 'report',
        clickMetadata: { section: 'post' },
      });
      dispatch(showModal({
        modalType: ModalType.REPORT,
        modalProps: { postId: post.id },
      }));
    } else {
      const reportContext = {
        element: signupContext.element.REPORT,
        section: signupContext.section.POST,
      };
      trackFootfallEvent(EventType.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: reportContext,
      });
      dispatch(showSignupModal(reportContext));
    }
  };

  return (
    <div className="post-actions">
      <div className="post-actions__buttons">
        {!post.isLocked && (isCommunityMember || !currentUser) && (
          <Button
            dataTestId="reply-button"
            size="sm"
            onClick={onReplyClick}
          >
            {i18n.t('Reply')}
          </Button>
        )}
        <LikeButton
          community={currentCommunity}
          currentUser={currentUser}
          isActive={!!currentUser}
          isDisabled={isCurrentUserPost}
          post={post}
          signupContext={{
            section: signupContext.section.POST,
            element: signupContext.element.LIKE,
          }}
        />
        {(canFollow || !currentUser) && (
          <FollowButton
            className="post-action"
            followIds={[post.id]}
            followType="post"
            isActive={!!currentUser}
            isFollowing={post.isFollowing}
            noIcon={true}
            signupContext={{
              section: signupContext.section.POST,
              element: signupContext.element.FOLLOW,
            }}
            silent={true}
            size="sm"
            trackLabel={post.urlEncodedTitle}
          >
            {i18n.t('Save post')}
          </FollowButton>
        )}
        {(!currentUser || canReport) &&
          <ReportButton
            className="btn"
            onClick={onReportClick}
          >
            {i18n.t('Report')}
          </ReportButton>
        }
        {currentUser && (
          <ActionsDropdown
            buttonText={i18n.t('More')}
            color="actions"
            dataTestId="spp-more-dropdown"
            post={post}
            superPost={post}
          />
        )}
      </div>
    </div>
  );
}

const ReportButton = styled.button`
  ${textLinkPrimary};
  background: none;
  border: none;
  padding: 6px 5px 6px 0px;
`;
